import { isPlatformServer } from '@angular/common';
import { Inject, Injectable, PLATFORM_ID, inject } from '@angular/core';
import { Router } from '@angular/router';
import { ApiService } from '@core/services/api.service';
import { ApiServiceHolder } from '@core/utils/api-service-holder';
import { TranslateService } from '@ngx-translate/core';
import { ILanguage } from '@shared/interfaces/language.interface';
import { BehaviorSubject } from 'rxjs';

const LOCALES = {
  it: 'it_IT',
  en: 'en_US',
};

export type ILanguageWithLocale = ILanguage & {
  locale: (typeof LOCALES)[keyof typeof LOCALES];
};

@Injectable({
  providedIn: 'root',
})
export class LanguageService {
  private _selectedLanguage = new BehaviorSubject<ILanguageWithLocale>(null);
  public selectedLanguage$ = this._selectedLanguage.asObservable();

  private readonly router = inject(Router);
  private readonly translate = inject(TranslateService);

  constructor(
    private _api: ApiService,
    @Inject(PLATFORM_ID) private _platformId: Object
  ) {}

  AppLanguageList: Array<ILanguage> = [];

  get userSelectedLanguage(): ILanguageWithLocale {
    return this._selectedLanguage.value;
  }

  set userSelectedLanguage(language: ILanguage) {
    this._selectedLanguage.next({
      ...language,
      locale: LOCALES[language.code] ?? 'en_US',
    });

    this.translate.setDefaultLang(language.code);
    this.translate.use(language.code);
  }

  fillLanguages() {
    this._api
      .request<Array<ILanguage>>(ApiServiceHolder.translation.getAllLanguages())
      .subscribe((dataR) => {
        this.AppLanguageList = dataR;
      });
  }

  getWindow() {
    if (isPlatformServer(this._platformId)) {
      return null;
    }

    return window;
  }

  changeLanguage(lang: ILanguage) {
    if (this.userSelectedLanguage.idLanguage === lang.idLanguage) {
      return;
    }

    const [url] = this.router.url.split('?');

    let urlParts = url.split('/');
    const currentLang = urlParts[1];

    if (currentLang === 'it' || currentLang === 'en') {
      urlParts.splice(0, 2);
    }

    localStorage.setItem('langId', lang.idLanguage.toString());
    localStorage.setItem('langCode', lang.code);

    this.userSelectedLanguage = {
      idLanguage: parseInt(localStorage.getItem('langId')),
      code: localStorage.getItem('langCode'),
      description: '',
    };
    this.router.navigate([lang.code, ...urlParts], {
      onSameUrlNavigation: 'reload',
      queryParamsHandling: 'merge',
      skipLocationChange: false,
    });
  }
}
